/**
 * PublishProgressHeaderStrip.tsx — PLAT-10947
 *
 * The clip-preview modal's in-header presentation of publish progress. Same
 * store as `PublishProgressDrawer`, but laid *into* the modal's chrome instead
 * of floating over it, so a publish started inside the modal reads as part of
 * the modal rather than an overlay landing on top of it.
 *
 * Status only: one line of status + counts + percent + progress bar, via
 * `PublishProgressInlineRow`. Two things the drawer has are deliberately absent
 * here, because the modal already provides them:
 *
 *  - **The variant list.** The Downloads tab sits directly below this strip and
 *    lists every variant with its status and the same Share Live Link +
 *    Download actions, plus the timestamp, publisher and Current badge. An
 *    expandable panel here duplicated that tab while covering it, so there is no
 *    caret and no dropdown — the tab *is* the list in this context.
 *  - **The dismiss (X).** It would sit inches from the modal's own close button
 *    and read as ambiguous. Dismissing stays with the floating drawer.
 *
 * The counts and percent ("1 of 2 · 55%") are NOT dropped, even though the bar
 * sits right beside them — the figures are what people read off, and the strip
 * is sized to carry both rather than the reverse.
 *
 * Placement: absolutely positioned inside the shared `Modal`'s `Content` (which
 * is `position: relative`), in the empty band above the tab row and to the left
 * of the modal's close (X) — that button is `top: 8px; right: 8px` and ~42px
 * wide, hence `CLOSE_BUTTON_GUTTER`. The strip's height is capped to that band
 * (`STRIP_HEIGHT`) so it can never reach the tabs: `RightPane`'s tab container
 * reserves `padding-top: 47px` before a 36px tab row, and the strip lives
 * entirely inside that 47px. Nothing here expands, so that holds at every stage
 * of a publish.
 *
 * Horizontally it stays inside the right column by construction: `Main` is a
 * `1fr 380px` grid with `padding: 0 24px`, so the right column spans 24–404px
 * from the modal's right edge. The strip is right-anchored at 70px and sizes to
 * its content, so it grows leftward only as far as `STRIP_MAX_WIDTH` allows —
 * never past 404px, and so never over the left pane's title.
 *
 * While hosting it registers as a header host, which stands the floating drawer
 * down (`PublishTrackingStore.hasHeaderHost`) so progress shows in exactly one
 * place. On unmount — i.e. when the modal closes — the drawer takes over again
 * with the same live state, variant list and X included.
 *
 * Dismissing the drawer stops watching every group, so a publish that is still
 * running loses its surface entirely. Reopening the clip it belongs to brings
 * that one back: the strip re-watches its own clip's live groups on mount, and
 * nobody else's — so the other clips the user dismissed stay dismissed.
 *
 * It only hosts its OWN clip's publish (PLAT-11395). The strip is this clip's
 * header chrome, so another clip's render reported here reads as "the clip
 * you're looking at is publishing" — and the Downloads tab below it, which the
 * strip's counts appear to summarise, lists this clip's renders only. A publish
 * of any other clip therefore stays with the floating drawer, which names each
 * clip in its variant list.
 */

import { useStore } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';

import {
  PublishProgressInlineRow,
  usePublishProgressView,
} from './publishProgressContent';

/**
 * Clears the shared Modal's close button: its own `right: 8px`, its ~42px
 * width, then a 20px gap. The gap is generous on purpose — the progress bar
 * ends at the strip's right edge, so a tight gap put a moving bar directly
 * against the X and made the X look like part of the progress widget.
 */
const CLOSE_BUTTON_GUTTER = 8 + 42 + 20;
/**
 * Height of the strip. `ShareModalTabs` reserves `padding-top: 47px` above its
 * 36px tab row, and the strip starts at `top: 8px` — so 8 + 30 = 38 leaves a
 * 9px gap before the tabs begin. One line of content is 20px, so 30px centres
 * it with 5px either side.
 */
const STRIP_HEIGHT = 30;
/**
 * The strip sizes to its content (status + counts + the fixed 140px bar), so it
 * only takes the room it needs — this is the ceiling, not the width.
 *
 * 334px is the hard limit and this sits on it: `Main` is a `1fr 380px` grid with
 * `padding: 0 24px`, so the right column spans 24–404px from the modal's right
 * edge, and the strip starts at `CLOSE_BUTTON_GUTTER` (70px) — 404 − 70 = 334.
 * Wider would push the strip into the left pane's column, over the modal title.
 * Widening the gutter therefore costs room here; the two move together.
 *
 * Only the longest status line ("Complete 12 of 12 · 3 failed · 100%") reaches
 * this, and it degrades by ellipsizing the counts rather than clipping the bar.
 */
const STRIP_MAX_WIDTH = 334;

export const PublishProgressHeaderStrip = observer(
  function PublishProgressHeaderStrip({ clipId }: { clipId: string }) {
    const { publishTrackingStore } = useStore();
    // Scoped to the open clip: with no publish of its own the strip shows
    // nothing and hosts nothing, leaving another clip's publish to the drawer.
    const view = usePublishProgressView(clipId);

    // Re-watch this clip's still-running publish, which a dismissed drawer
    // stopped watching. Keyed on the joined ids rather than the array: the view
    // rebuilds it every render, and a fresh-array dep would re-fire the effect
    // each time (pitfall 18).
    const liveGroups = view.liveGroupIds.join(',');
    useEffect(() => {
      if (liveGroups) publishTrackingStore.watchGroups(liveGroups.split(','));
    }, [publishTrackingStore, liveGroups]);

    // Claim the surface for as long as there is something of this clip's to
    // show. A layout effect, not a plain one: the claim has to land before the
    // browser paints the commit that made the strip visible, or a publish
    // dispatched from inside the modal flashes the drawer for a frame first.
    useLayoutEffect(() => {
      if (!view.isVisible) return;
      publishTrackingStore.registerHeaderHost();
      return () => publishTrackingStore.unregisterHeaderHost();
    }, [publishTrackingStore, view.isVisible]);

    if (!view.isVisible) return null;

    return (
      <Strip data-testid="publish-progress-header-strip" role="status">
        <PublishProgressInlineRow view={view} />
      </Strip>
    );
  },
);

/* Reads as modal chrome, not a card set into it: no border, no fill, no shadow,
   no slide-in — just the line of content sitting in the header band the way the
   title and tabs do. Height is capped with `overflow: hidden` so a long status
   line can never bleed down onto the tabs. */
const Strip = styled.div`
  position: absolute;
  top: 8px;
  right: ${CLOSE_BUTTON_GUTTER}px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: max-content;
  max-width: ${STRIP_MAX_WIDTH}px;
  height: ${STRIP_HEIGHT}px;
  overflow: hidden;
  box-sizing: border-box;
  text-align: left;
`;
